/**
 * PORT (touch): finding what a finger meant on a UI made for the mouse. The game's 800x600 stage is drawn at ~0.69
 * CSS px per stage px on a phone in landscape, so its buttons are 10-30 CSS px (below the 44-48 px touch guideline)
 * while a fingertip covers ~10 mm. Without changing the UI, a finger that misses every button presses the nearest one
 * within a slop radius when that choice is unambiguous (BrowserInput, touch only; the mouse is never retargeted).
 *
 * "Clickable" is what shows the hand cursor in Flash: an enabled SimpleButton, or a Sprite/MovieClip with buttonMode
 * (the pick target or its ancestor). Hits that stay exact: a clickable, any text field, and a small element that is
 * a target of its own (both sides within `ownTarget` stage px: an icon, an info box; not the frame of a button inside
 * it). Only a miss, a button's frame or a larger backdrop (a screen, a panel, a list's background) may snap to a
 * button nearby, e.g. a finger in a hole of an icon button's shape.
 */
import { TWIPS, rectValid, rectUnion, toTwips, type TRect } from '../utils/numeric';

/** Topmost interactive object under a stage point (the player's mouse pick at that point), or null (the stage). */
export function pickAt(stage: any, x: number, y: number): any {
  if (!stage || typeof stage.$mousePick !== 'function') return null;
  const res = stage.$mousePick(toTwips(x), toTwips(y));
  return res && res.kind === 'hit' && res.target !== stage ? res.target : null;
}

/** The button a press on `obj` reaches: the SimpleButton or buttonMode Sprite at or above it (hand cursor), else null. */
export function clickableOf(obj: any): any {
  for (let o = obj; o && !o.$isStage; o = o.$parent) {
    if (o.$isButton) return o;
    if (o.$buttonMode) return o;
  }
  return null;
}

function isInside(obj: any, container: any): boolean {
  for (let o = obj; o; o = o.$parent) if (o === container) return true;
  return false;
}

function isTextField(obj: any): boolean {
  return !!obj && obj.$renderKind === 'text';
}

function shownOnStage(o: any): boolean {
  for (let a = o; a; a = a.$parent) {
    if (!a.$visible) return false;
    if (a.$isStage) return true;
  }
  return false;
}

/** World bounds (twips) of a candidate: a button's shown state and its hit area. */
function candidateBounds(o: any): TRect {
  let r: TRect = o.$worldBounds();
  const hit = o.$isButton ? o.$hitTestState : o.$hitArea;
  if (hit && typeof hit.$worldBounds === 'function') r = rectUnion(r, hit.$worldBounds());
  return r;
}

/** Enabled, mouse-enabled buttons shown on the stage whose bounds come within `radius` (stage px) of the point. */
function nearbyButtons(stage: any, x: number, y: number, radius: number): Array<{ obj: any; d: number; r: TRect }> {
  const out: Array<{ obj: any; d: number; r: TRect }> = [];
  const gx = toTwips(x);
  const gy = toTwips(y);
  const rt = toTwips(radius);
  const consider = (o: any): void => {
    if (!o.$mouseEnabled || o.$enabled === false) return;
    const r = candidateBounds(o);
    if (!rectValid(r)) return;
    const dx = Math.max(r.xMin - gx, 0, gx - r.xMax);
    const dy = Math.max(r.yMin - gy, 0, gy - r.yMax);
    const d = Math.hypot(dx, dy);
    if (d <= rt) out.push({ obj: o, d: d / TWIPS, r });
  };
  const walk = (o: any): void => {
    if (!o || !o.$visible || o.$maskee || o.$clipDepth > 0) return;
    if (o.$isButton) return consider(o);
    if (o.$buttonMode && !o.$isStage) consider(o);
    // A container that keeps its children from the mouse is the target itself (a buttonMode sprite, considered above).
    if (!o.$isContainer || o.$mouseChildren === false) return;
    const kids = o.$children;
    for (let i = 0; i < kids.length; i++) if (kids[i].$isInteractive) walk(kids[i]);
  };
  walk(stage);
  return out;
}

/**
 * The point nearest to the finger, inside the candidate's bounds, that the player's pick resolves to the candidate (its
 * shape may have holes or be partly covered): the nearest point of the bounds, then the way to its centre and a 5x5
 * grid over it, nearest first; null when none is reachable.
 */
function reachablePoint(stage: any, obj: any, r: TRect, x: number, y: number): [number, number] | null {
  const x0 = r.xMin / TWIPS;
  const y0 = r.yMin / TWIPS;
  const x1 = r.xMax / TWIPS;
  const y1 = r.yMax / TWIPS;
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  // The nearest point of the bounds, one stage px inside (the centre of a sliver).
  const nx = x1 - x0 > 2 ? Math.min(Math.max(x, x0 + 1), x1 - 1) : cx;
  const ny = y1 - y0 > 2 ? Math.min(Math.max(y, y0 + 1), y1 - 1) : cy;
  const pts: Array<[number, number]> = [];
  for (const t of [0.34, 0.67, 1]) pts.push([nx + (cx - nx) * t, ny + (cy - ny) * t]);
  for (const fx of [0.1, 0.3, 0.5, 0.7, 0.9]) for (const fy of [0.1, 0.3, 0.5, 0.7, 0.9]) pts.push([x0 + (x1 - x0) * fx, y0 + (y1 - y0) * fy]);
  pts.sort((a, b) => Math.hypot(a[0] - x, a[1] - y) - Math.hypot(b[0] - x, b[1] - y));
  for (const [px, py] of [[nx, ny] as [number, number], ...pts]) if (clickableOf(pickAt(stage, px, py)) === obj) return [px, py];
  return null;
}

export interface TouchSnapOptions {
  /** Slop radius (stage px): buttons whose bounds are farther from the finger are not considered. */
  radius: number;
  /** Two buttons whose distances differ by less than this (stage px) are a tie: neither is pressed. */
  tie: number;
  /** A non-clickable hit whose both sides are at most this (stage px) is a target of its own (default 0: none). */
  ownTarget?: number;
}

/**
 * Where a finger at stage point (x, y) presses: null when it stays where it is (it hit a button, a text field or a
 * small element of its own, or no button is near enough, or two are about as near); otherwise a point inside the
 * nearest shown, enabled button that the player's pick reaches there (not covered by anything), with that button.
 */
export function snapTouch(stage: any, x: number, y: number, opts: TouchSnapOptions): { x: number; y: number; target: any } | null {
  if (!stage || typeof stage.$mousePick !== 'function' || !(opts.radius > 0)) return null;
  const hit = pickAt(stage, x, y);
  if (hit && (clickableOf(hit) || isTextField(hit))) return null;
  const near = nearbyButtons(stage, x, y, opts.radius).sort((a, b) => a.d - b.d);
  // A small element of its own (an icon, an info box) keeps the finger, unless it is the frame of a button inside it.
  if (hit && opts.ownTarget && opts.ownTarget > 0 && !near.some((c) => isInside(c.obj, hit))) {
    const b: TRect = hit.$worldBounds();
    if (rectValid(b) && (b.xMax - b.xMin) / TWIPS <= opts.ownTarget && (b.yMax - b.yMin) / TWIPS <= opts.ownTarget) return null;
  }
  let best: { obj: any; d: number; p: [number, number] } | null = null;
  for (const c of near) {
    if (best && c.d - best.d >= opts.tie) break;
    if (!shownOnStage(c.obj)) continue;
    const p = reachablePoint(stage, c.obj, c.r, x, y);
    if (!p) continue;
    if (best) return null; // a second reachable button about as near: ambiguous
    best = { obj: c.obj, d: c.d, p };
  }
  return best ? { x: best.p[0], y: best.p[1], target: best.obj } : null;
}
